import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";

import { faCirclePlay } from "@fortawesome/pro-regular-svg-icons/faCirclePlay";
import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuProps } from "antd/es/menu";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import AccessStatus from "@mapmycustomers/shared/types/entity/common/AccessStatus";
import FileTag from "@mapmycustomers/shared/types/entity/FileTag";
import File, { RawFile } from "@mapmycustomers/shared/types/File";
import { Dropdown, LoadingSpinner } from "@mapmycustomers/ui";

import AddTagButton from "@app/component/AddTagButton";
import FileTags from "@app/component/FileTags";
import MoreButton from "@app/component/MoreButton";
import hasMimeType, { VIDEO_ANY } from "@app/util/mimeType";

import styles from "./FileThumbnail.module.scss";

interface Props {
  accessStatus?: AccessStatus;
  canDelete?: boolean;
  className?: string;
  deleteTooltip?: string;
  editingTags?: boolean;
  file: RawFile;
  onChange?: (file: RawFile) => void;
  onClick?: (file: RawFile) => void;
  onDelete?: (file: RawFile) => void;
  onDownload: (file: RawFile) => void;
  onFetchThumbnail?: (payload: { callback: (blob: Blob) => void; fileId: RawFile["id"] }) => void;
  supportsTags?: boolean;
  visible?: boolean;
}

const FileThumbnail = ({
  accessStatus,
  canDelete = true,
  className,
  deleteTooltip,
  editingTags,
  file,
  onChange,
  onClick,
  onDelete,
  onDownload,
  onFetchThumbnail,
  supportsTags,
  visible,
}: Props) => {
  const intl = useIntl();
  const [thumbnailUrl, setThumbnailUrl] = useState<string | undefined>();
  const [isLoading, setLoading] = useState(false);
  const isVideo = hasMimeType(file, VIDEO_ANY);
  const [menuVisible, setMenuVisible] = useState(false);

  useEffect(() => {
    // don't load thumbnail if:
    // - it's invisible
    // - it's a video
    // - loaded it already
    if (!visible || thumbnailUrl || isVideo || !onFetchThumbnail) {
      return;
    }

    setLoading(true);
    onFetchThumbnail({
      callback: (blob) => {
        setThumbnailUrl(URL.createObjectURL(blob));
        setLoading(false);
      },
      fileId: file.id,
    });
  }, [visible, onFetchThumbnail, thumbnailUrl, file.id, isVideo]);

  useEffect(
    () => () => {
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    },
    [thumbnailUrl]
  );

  const handleClick = useCallback(() => {
    onClick?.(file);
    setMenuVisible(false);
  }, [file, onClick]);

  const handleDelete = useCallback(
    (e: MenuInfo) => {
      e.domEvent.stopPropagation();
      onDelete?.(file);
      setMenuVisible(false);
    },
    [file, onDelete]
  );

  const handleDownload = useCallback(
    (e: MenuInfo) => {
      e.domEvent.stopPropagation();
      onDownload?.(file);
      setMenuVisible(false);
    },
    [file, onDownload]
  );

  const handleMenuClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMenuVisible((visible) => !visible);
  }, []);

  const handleTagsChange = useCallback(
    (tags: File["tags"]) => {
      onChange?.({ ...file, tags });
    },
    [file, onChange]
  );
  const handleRemoveTag = useCallback(
    (tagId: FileTag["id"]) => {
      onChange?.({ ...file, tags: file.tags?.filter(({ id }) => id !== tagId) });
    },
    [file, onChange]
  );

  const menu: MenuProps = useMemo(
    () => ({
      items: [
        {
          icon: <FontAwesomeIcon className={styles.downloadIcon} icon={faDownload} />,
          key: "download",
          label: intl.formatMessage({
            id: "fileContent.files.section.media.menu.download",
            defaultMessage: "Download",
            description: "Download file item label in Files Carousel on the Preview Pane",
          }),
          onClick: handleDownload,
        },
        ...(onDelete
          ? [
              {
                disabled: !accessStatus?.delete || !canDelete,
                icon: <FontAwesomeIcon className={styles.deleteIcon} icon={faTrash} />,
                key: "delete",
                label: (
                  <Tooltip placement="left" title={deleteTooltip ?? null}>
                    <span>
                      {intl.formatMessage({
                        id: "fileContent.files.section.media.menu.delete",
                        defaultMessage: "Delete",
                        description: "Delete file item label in Files Carousel on the Preview Pane",
                      })}
                    </span>
                  </Tooltip>
                ),
                onClick: handleDelete,
              },
            ]
          : []),
      ],
    }),
    [accessStatus?.delete, canDelete, deleteTooltip, handleDelete, handleDownload, intl, onDelete]
  );

  return (
    <div className={cn(styles.container, className)}>
      <div
        className={cn(styles.imageContainer, { [styles.videoContainer]: isVideo })}
        onClick={handleClick}
      >
        {isLoading ? (
          <LoadingSpinner className={styles.loader} mini />
        ) : isVideo ? (
          <div className={styles.video}>
            <FontAwesomeIcon icon={faCirclePlay} size="3x" />
          </div>
        ) : (
          <img alt={file.name} className={styles.image} src={thumbnailUrl} />
        )}
        <Tooltip title={file.name}>
          <div className={styles.overlay}>
            <Dropdown
              menu={menu}
              onOpenChange={setMenuVisible}
              open={menuVisible}
              placement="bottomRight"
              trigger={["click"]}
            >
              <MoreButton className={styles.moreButton} onClick={handleMenuClick} type="link" />
            </Dropdown>
          </div>
        </Tooltip>
      </div>
      {supportsTags && (
        <div className={styles.tags}>
          {file.tags && file.tags.length > 0 && (
            <FileTags
              className={styles.fileTags}
              onRemoveTag={editingTags ? handleRemoveTag : undefined}
              tags={file.tags}
            />
          )}
          {editingTags && <AddTagButton onChange={handleTagsChange} tags={file.tags} />}
        </div>
      )}
    </div>
  );
};

export default FileThumbnail;
